import { css } from 'styled-components';

/**
 * "Overline" type style — UPPERCASE tracked-out labels for column heads,
 * stat-card captions, eyebrow labels, etc. Matches the design bundle's
 * .text-overline pattern. Caller supplies a color override if needed.
 */
export const overlineLabel = css`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/**
 * Pill — fully-rounded inline-flex chip used for status badges, filter pills,
 * label tags. Doesn't set colors — caller supplies bg + color.
 */
export const pillShape = css`
  display: inline-flex;
  align-items: center;
  padding: 0.2em 0.65em;
  border-radius: ${({ theme }) => theme.radii.full};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  line-height: 1.4;
`;

/**
 * Single-line text with ellipsis — useful for filenames, sygnatura, etc.
 */
export const truncateLine = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * "Card surface" — the white-bordered-rounded-shadowed container used for
 * SurfaceCard, the DataTable wrapper, info tables, etc.
 */
export const cardSurface = css`
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.radii.xl};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

/**
 * Page-heading typography — EB Garamond, navy primary, slightly tight tracking.
 * Caller supplies font-size and font-weight (since they vary by level: PageTitle
 * is 2xl/bold, SectionTitle is lg/semibold, etc.).
 */
export const headingFont = css`
  font-family: ${({ theme }) => theme.fonts.heading};
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: -0.005em;
`;

/**
 * Monospace numeric — tabular-nums style for right-aligned numbers,
 * sygnatura, hashes, etc.
 */
export const monoNumeric = css`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-variant-numeric: tabular-nums;
`;

/**
 * Visually hide but keep available to screen readers.
 * (Mirrors the .sr-only utility class.)
 */
export const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * Navy focus halo — 3px box-shadow ring using primary at low opacity.
 * Matches the design-bundle a11y spec for form-control :focus state.
 */
export const focusRingNavy = css`
  box-shadow: 0 0 0 3px rgba(27, 42, 74, 0.08);
`;

/**
 * Gold focus halo — 3px ring at low opacity. Used for form-input :focus state
 * on light surfaces per the design bundle.
 */
export const focusRingGold = css`
  box-shadow: 0 0 0 3px rgba(212, 168, 67, 0.15);
`;
